/**
 * Agora RTC credential contract.
 *
 * The App Certificate never appears here. It lives only in apps/api, which is
 * the sole minter of tokens (ARCHITECTURE.md §3, ADR-0009) — no client bundle,
 * native or web, may ever carry it.
 */

import { z } from 'zod';
import { channelSchema } from './session';

/** Both parties publish: this is a 1:1 consultation, not a broadcast. */
export const RTC_ROLES = ['physio', 'patient'] as const;
export type RtcRole = (typeof RTC_ROLES)[number];

export const agoraCredentialsSchema = z.object({
  ok: z.literal(true),
  token: z.string().min(1),
  /** Client-safe App ID. Distinct from the certificate, which is server-only. */
  appId: z.string().min(1),
  channel: channelSchema,
  uid: z.number().int().min(0),
  role: z.enum(RTC_ROLES),
  expiresInSeconds: z.number().int().positive(),
});
export type AgoraCredentials = z.infer<typeof agoraCredentialsSchema>;

/**
 * Video orientation, published by BOTH clients.
 *
 * This is load-bearing and easy to lose: the spike forced fixed-portrait on the
 * publisher specifically so the recorded MP4 landed portrait rather than
 * sideways. Recording does not exist yet in the walking skeleton, so getting
 * this wrong would produce no visible symptom until the capture capsule
 * graduates — and would then look like a recording bug rather than a join-path
 * one. Encoded here so both surfaces read it from the same place.
 */
export const VIDEO_ORIENTATION = 'fixed-portrait' as const;

/**
 * Channel profile. The spike sets ChannelProfileCommunication on both sides;
 * the web client's equivalent is mode 'rtc'.
 */
export const AGORA_CHANNEL_PROFILE = 'communication' as const;

/**
 * Video codec — settles ADR-0007 §5, which deferred to "whatever the native
 * capsule publishes/subscribes; spike evidence decides".
 *
 * The evidence, and why the answer is not "leave it unset": the native spike
 * sets no codec at all, so it runs the Agora *native* SDK default, which is
 * H.264. The web SDK's default is not H.264 and has moved across versions. So
 * matching native means the web client must pin h264 EXPLICITLY — leaving both
 * sides "default" would silently mismatch them.
 *
 * A mismatch here does not fail loudly. It degrades: the SDN transcodes or the
 * remote video simply never renders, which reads as a network problem rather
 * than a configuration one. That is why this is a shared constant and not a
 * literal in the client.
 */
export const AGORA_VIDEO_CODEC = 'h264' as const;
